/**
 * SuggestionCards Component
 * =========================
 * Displays proactive suggestions from Orion with approve/reject/snooze actions.
 * Uses polling for real-time updates.
 * 
 * Feature Flag: ENABLE_SUGGESTIONS (behind flag for gradual rollout)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';
import { fetchWithOrg } from '../../utils/api';

import {
  ArrowsClockwise,
  Calendar,
  CaretDown,
  CaretUp,
  Check,
  CircleNotch,
  Clock,
  Cpu,
  HardDrive,
  Sparkle,
  WarningCircle,
  WifiHigh,
  WifiSlash,
  X
} from '@phosphor-icons/react';
// Feature flag - can be controlled via environment or props
const ENABLE_SUGGESTIONS = true;

// Polling interval for real-time updates (5 seconds)
const POLL_INTERVAL_MS = 5000;

// Suggestion type icons and colors
const SUGGESTION_TYPE_CONFIG = {
  connector_offline: {
    icon: WifiSlash,
    color: 'red',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    iconColor: 'text-[#FF6B6B]'
  },
  // NEW: Repeated task automation suggestion
  repeated_task: {
    icon: ArrowsClockwise,
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    iconColor: 'text-blue-400'
  },
  // NEW: Daily briefing prompt
  daily_briefing: {
    icon: Calendar,
    color: 'purple',
    bgColor: 'bg-[#00E5C0]/10',
    borderColor: 'border-purple-500/30',
    iconColor: 'text-[#00E5C0]'
  },
  // NEW: Low disk space
  disk_space: {
    icon: HardDrive,
    color: 'orange',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    iconColor: 'text-orange-400'
  },
  // NEW: High memory usage
  high_memory: {
    icon: Cpu,
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400'
  },
  system_health: {
    icon: WarningCircle,
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400'
  },
  scheduled_task: {
    icon: Clock,
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    iconColor: 'text-blue-400'
  },
  proactive_action: {
    icon: Sparkle,
    color: 'purple',
    bgColor: 'bg-[#00E5C0]/10',
    borderColor: 'border-purple-500/30',
    iconColor: 'text-[#00E5C0]'
  },
  custom: {
    icon: WarningCircle,
    color: 'yellow',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    iconColor: 'text-[#F59E0B]'
  }
};

const SuggestionCard = ({ suggestion, onApprove, onReject, onSnooze, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  const config = SUGGESTION_TYPE_CONFIG[suggestion.suggestion_type] || SUGGESTION_TYPE_CONFIG.custom;
  const TypeIcon = config.icon;
  
  const handleAction = async (action, actionId = null) => {
    setActionLoading(action);
    try {
      if (action === 'approve') {
        await onApprove(suggestion.id, actionId);
      } else if (action === 'reject') {
        await onReject(suggestion.id);
      } else if (action === 'snooze') {
        await onSnooze(suggestion.id, 2); // Default 2 hours
      }
    } finally {
      setActionLoading(null);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10, height: 0 }}
      className={`rounded-xl border ${config.borderColor} ${config.bgColor} p-4 mb-3`}
      data-testid={`suggestion-card-${suggestion.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.bgColor}`}>
          <TypeIcon size={20} className={config.iconColor} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white text-sm">
            {suggestion.observation}
          </h4>
          <p className="text-[#A0AEC0] text-xs mt-1">
            {suggestion.interpretation}
          </p>
        </div>
        
        {/* Expand/Collapse for actions */}
        {suggestion.proposed_actions?.length > 0 && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-white/5 rounded"
            data-testid={`suggestion-expand-${suggestion.id}`}
          >
            {expanded ? <CaretUp size={16} className="text-[#A0AEC0]" /> : <CaretDown size={16} className="text-[#A0AEC0]" />}
          </button>
        )}
      </div>
      
      {/* Expanded Actions */}
      <AnimatePresence>
        {expanded && suggestion.proposed_actions?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 pt-3 border-t border-white/10"
          >
            <p className="text-xs text-[#A0AEC0] mb-2">Proposed Actions:</p>
            <div className="space-y-2">
              {suggestion.proposed_actions.map((action, idx) => (
                <div 
                  key={action.id || idx}
                  className="flex items-center justify-between p-2 rounded-lg bg-black/20"
                >
                  <span className="text-sm text-white">{action.title}</span>
                  <button
                    onClick={() => handleAction('approve', action.id)}
                    disabled={actionLoading}
                    className="px-3 py-1 text-xs rounded-lg bg-green-500/20 text-[#00E5C0] hover:bg-green-500/30 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`suggestion-approve-action-${action.id}`}
                  >
                    {actionLoading === 'approve' ? (
                      <CircleNotch size={12} className="animate-spin" />
                    ) : (
                      <Check size={12} />
                    )}
                    Approve
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
        {/* If no expanded actions or simple suggestion, show approve button */}
        {(!suggestion.proposed_actions || suggestion.proposed_actions.length === 0) && (
          <button
            onClick={() => handleAction('approve')}
            disabled={actionLoading}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-green-500/20 text-[#00E5C0] hover:bg-green-500/30 disabled:opacity-50 transition-all"
            data-testid={`suggestion-approve-${suggestion.id}`}
          >
            {actionLoading === 'approve' ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <Check size={14} />
            )}
            Approve
          </button>
        )}
        
        <button
          onClick={() => handleAction('snooze')}
          disabled={actionLoading}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-yellow-500/20 text-[#F59E0B] hover:bg-yellow-500/30 disabled:opacity-50 transition-all"
          data-testid={`suggestion-snooze-${suggestion.id}`}
        >
          {actionLoading === 'snooze' ? (
            <CircleNotch size={14} className="animate-spin" />
          ) : (
            <Clock size={14} />
          )}
          Snooze
        </button>
        
        <button
          onClick={() => handleAction('reject')}
          disabled={actionLoading}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-red-500/20 text-[#FF6B6B] hover:bg-red-500/30 disabled:opacity-50 transition-all"
          data-testid={`suggestion-reject-${suggestion.id}`}
        >
          {actionLoading === 'reject' ? (
            <CircleNotch size={14} className="animate-spin" />
          ) : (
            <X size={14} />
          )}
          Dismiss
        </button>
      </div>
      
      {/* Timestamp */}
      <div className="text-[10px] text-[#A0AEC0]/60 mt-2 text-right">
        {new Date(suggestion.created_at).toLocaleString()}
      </div>
    </motion.div>
  );
};

export const SuggestionCards = ({ agentId, personaName = 'Orion', onSuggestionAction }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  
  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!agentId || !ENABLE_SUGGESTIONS) return;
    
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/exec/suggestions/${agentId}?limit=5`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setError(null);
      }
    } catch (err) {
      console.error('[SUGGESTIONS] Fetch error:', err);
      setError('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [agentId]);
  
  // Initial fetch and polling
  useEffect(() => {
    fetchSuggestions();
    
    // Poll for real-time updates
    const interval = setInterval(fetchSuggestions, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchSuggestions]);
  
  // Handle approve
  const handleApprove = async (suggestionId, actionId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/suggestions/${suggestionId}/approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action_id: actionId || 'default' })
        }
      );
      
      if (response.ok) {
        toast.success('Action approved');
        // Remove from local state immediately for responsiveness
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        onSuggestionAction?.('approve', suggestionId);
      } else {
        toast.error('Failed to approve action');
      }
    } catch (err) {
      toast.error('Failed to approve action');
    }
  };
  
  // Handle reject/dismiss
  const handleReject = async (suggestionId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/suggestions/${suggestionId}/dismiss`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );
      
      if (response.ok) {
        toast.success('Suggestion dismissed');
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        onSuggestionAction?.('reject', suggestionId);
      } else {
        toast.error('Failed to dismiss suggestion');
      }
    } catch (err) {
      toast.error('Failed to dismiss suggestion');
    }
  };
  
  // Handle snooze
  const handleSnooze = async (suggestionId, hours = 2) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/suggestions/${suggestionId}/snooze`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hours })
        }
      );
      
      if (response.ok) {
        toast.success(`Snoozed for ${hours} hours`);
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
        onSuggestionAction?.('snooze', suggestionId);
      } else {
        toast.error('Failed to snooze suggestion');
      }
    } catch (err) {
      toast.error('Failed to snooze suggestion');
    }
  };
  
  // Don't render if feature is disabled
  if (!ENABLE_SUGGESTIONS) return null;
  
  // Don't render if no suggestions and not loading
  if (!loading && suggestions.length === 0) return null;
  
  return (
    <div className="mb-6" data-testid="suggestion-cards-container">
      {/* Header */}
      <div 
        className="flex items-center justify-between mb-3 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <Sparkle size={16} className="text-[#00E5C0]" />
          <span className="text-sm font-medium text-white">
            {personaName}&apos;s Suggestions
          </span>
          {suggestions.length > 0 && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-[#00E5C0]/20 text-[#00E5C0]">
              {suggestions.length}
            </span>
          )}
        </div>
        <button className="p-1 hover:bg-white/5 rounded">
          {collapsed ? <CaretDown size={16} className="text-[#A0AEC0]" /> : <CaretUp size={16} className="text-[#A0AEC0]" />}
        </button>
      </div>
      
      {/* Content */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <CircleNotch size={20} className="animate-spin text-[#00E5C0]" />
              </div>
            ) : error ? (
              <div className="text-center py-4 text-[#FF6B6B] text-sm">
                {error}
              </div>
            ) : (
              <div>
                {suggestions.map(suggestion => (
                  <SuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onSnooze={handleSnooze}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SuggestionCards;
